import { AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';
import { X, Building2, Clock, Wallet, Boxes } from 'lucide-react';
import { ModalRoot } from '@/components/ui/ModalRoot';
import { Z_MODALS } from '@/lib/zIndex';
import { STATION_SPEC_LIST, getCurrentTier, isVehicleStationKind } from '@/data/stations';
import type { HospitalTier, VehicleStationTier } from '@/data/stations';
import { formatEuro } from '@/lib/format';
import { formatBuildTime } from '@/data/buildTime';
import { useGameStore } from '@/state/gameStore';
import type { StationKind } from '@/state/types';

interface StationTypePickerProps {
  open: boolean;
  credits: number;
  onClose: () => void;
  onSelect: (kind: StationKind) => void;
}

export function StationTypePicker({ open, credits, onClose, onSelect }: StationTypePickerProps) {
  const mode = useGameStore((s) => s.mode);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <ModalRoot
          onClose={onClose}
          zIndex={Z_MODALS}
          contentClass="w-full max-w-2xl rounded-lg border border-border-strong bg-bg-secondary p-6"
        >
            <div className="mb-4 flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-accent-dispatch">Bauen</p>
                <h3 id="station-picker-title" className="font-display text-xl font-semibold">
                  Wachen-Typ wählen
                </h3>
                <p className="mt-1 text-xs text-fg-muted">
                  Stufe 1 wird gebaut. Spätere Ausbau­stufen erweitern Slots, Unterhalt und
                  Bauzeit.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Schließen"
                className="text-fg-muted transition hover:text-fg-primary"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>

            <ul className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {STATION_SPEC_LIST.map((spec) => {
                const tier = getCurrentTier(spec.kind, 1);
                const cost = tier.cost;
                const affordable = mode === 'sandbox' || credits >= cost;
                const slotInfo = isVehicleStationKind(spec.kind)
                  ? { label: 'Slots', value: (tier as VehicleStationTier).vehicleSlots }
                  : { label: 'Patienten', value: (tier as HospitalTier).patientCapacity };

                return (
                  <li key={spec.kind}>
                    <button
                      type="button"
                      disabled={!affordable}
                      onClick={() => onSelect(spec.kind)}
                      className={[
                        'flex h-full w-full flex-col gap-3 rounded-md border p-4 text-left transition',
                        affordable
                          ? 'border-border-subtle bg-bg-elevated/40 hover:border-accent-dispatch hover:bg-bg-elevated'
                          : 'cursor-not-allowed border-border-subtle bg-bg-elevated/20 opacity-50',
                      ].join(' ')}
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md"
                          style={{ background: spec.markerColor }}
                        >
                          <Building2 className="h-4 w-4 text-white" aria-hidden />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="font-display text-sm font-bold">{spec.label}</p>
                          <p className="mt-0.5 text-[11px] text-fg-muted">{spec.description}</p>
                        </div>
                      </div>

                      <dl className="grid grid-cols-2 gap-1.5 text-[11px]">
                        <Stat icon={Wallet} label="Kosten">
                          {cost === 0 ? 'Kostenlos' : formatEuro(cost)}
                        </Stat>
                        <Stat icon={Clock} label="Bauzeit">
                          {formatBuildTime(tier.buildTimeHours, mode)}
                        </Stat>
                        <Stat icon={Wallet} label="Tägl.">
                          -{formatEuro(tier.upkeepPerDay)}
                        </Stat>
                        <Stat icon={Boxes} label={slotInfo.label}>
                          {slotInfo.value}
                        </Stat>
                      </dl>

                      {!affordable && (
                        <p className="text-[10px] uppercase tracking-wider text-accent-emergency">
                          Zu teuer
                        </p>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>

            <p className="mt-4 text-xs text-fg-muted">
              Nach der Auswahl klickst du in die Karte, um die Wache zu platzieren. ESC bricht ab.
            </p>
        </ModalRoot>
      )}
    </AnimatePresence>
  );
}

function Stat({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof Building2;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className="h-3 w-3 text-fg-muted" aria-hidden />
      <span className="text-fg-muted">{label}:</span>
      <span className="font-semibold tabular-nums text-fg-primary">{children}</span>
    </div>
  );
}
